import React, { useState, useEffect } from "react";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = "Enter phone number",
  className = "",
  required = false,
  disabled = false,
  name,
  id
}) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Remove any existing "+" from the input value
    const cleanValue = inputValue.replace(/^\+/, "");
    
    // Ensure the value always starts with "+"
    const formattedValue = cleanValue ? `+${cleanValue}` : "";
    
    setDisplayValue(formattedValue);
    onChange(formattedValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent backspace/delete when cursor is at position 1 (right after the "+")
    if (e.key === "Backspace" || e.key === "Delete") {
      const cursorPosition = e.currentTarget.selectionStart;
      if (cursorPosition <= 1) {
        e.preventDefault();
      }
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // If user clicks at the beginning, move cursor after the "+"
    const input = e.currentTarget;
    if (input.selectionStart === 0) {
      input.setSelectionRange(1, 1);
    }
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
    // Prevent selecting the "+" character
    const input = e.currentTarget;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    
    if (start === 0 && end > 0) {
      input.setSelectionRange(1, end);
    }
  };

  return (
    <div className="relative">
      <input
        type="tel"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onSelect={handleSelect}
        placeholder={placeholder}
        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${className}`}
        required={required}
        disabled={disabled}
        name={name}
        id={id}
        autoComplete="tel"
      />
    </div>
  );
};

export default PhoneInput;
